import { Glass, StatBar } from "@coach/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Camera, Mail, Pencil, Plus, Search, Shield, Trash2, Users, X } from "lucide-react";
import { type ChangeEvent, type FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { useConfirm } from "../features/canvas-kit/ui/confirm-dialog.jsx";
import { useSync } from "../providers/sync-provider";
import {
  createPlayer,
  deletePlayer,
  listPlayers,
  type PlayerRow,
  type PlayerStats,
  parseStats,
  updatePlayer,
} from "../repos/players";

/* ──────────────────────────── design tokens ──────────────────────────── */

const POSITIONS = ["TW", "IV", "LV", "RV", "ZM", "LM", "RM", "LS", "RS", "ST"] as const;

const FEET = ["links", "rechts", "beidfüßig"] as const;

const POSITION_TONE: Record<string, { bg: string; fg: string; ring: string }> = {
  TW: {
    bg: "rgba(255,176,46,0.18)",
    fg: "#ffd28a",
    ring: "rgba(255,176,46,0.5)",
  },
  IV: {
    bg: "rgba(120,170,255,0.18)",
    fg: "#bcd5ff",
    ring: "rgba(120,170,255,0.5)",
  },
  LV: {
    bg: "rgba(120,170,255,0.18)",
    fg: "#bcd5ff",
    ring: "rgba(120,170,255,0.5)",
  },
  RV: {
    bg: "rgba(120,170,255,0.18)",
    fg: "#bcd5ff",
    ring: "rgba(120,170,255,0.5)",
  },
  ZM: {
    bg: "rgba(151,252,0,0.16)",
    fg: "#d6ff8c",
    ring: "rgba(151,252,0,0.5)",
  },
  LM: {
    bg: "rgba(151,252,0,0.16)",
    fg: "#d6ff8c",
    ring: "rgba(151,252,0,0.5)",
  },
  RM: {
    bg: "rgba(151,252,0,0.16)",
    fg: "#d6ff8c",
    ring: "rgba(151,252,0,0.5)",
  },
  LS: {
    bg: "rgba(189,156,255,0.18)",
    fg: "#d8c6ff",
    ring: "rgba(189,156,255,0.5)",
  },
  RS: {
    bg: "rgba(189,156,255,0.18)",
    fg: "#d8c6ff",
    ring: "rgba(189,156,255,0.5)",
  },
  ST: {
    bg: "rgba(255,121,143,0.18)",
    fg: "#ffc1cc",
    ring: "rgba(255,121,143,0.5)",
  },
};

const NEUTRAL_TONE = {
  bg: "rgba(190,200,210,0.16)",
  fg: "#d5dde6",
  ring: "rgba(190,200,210,0.4)",
};

function toneOf(pos: string | null | undefined) {
  if (!pos) return NEUTRAL_TONE;
  return POSITION_TONE[pos] ?? NEUTRAL_TONE;
}

/**
 * Per-attribute color palette — one distinctive hue per stat so PlayerCard
 * and PlayerDialog stay visually aligned ("Ausdauer" is always lime, no
 * matter where it appears). Tone keys match `StatBar`'s `tone` prop so the
 * shared progress-bar component picks the right Tailwind fill class. The
 * CSS values below are used directly for the range-slider gradients in
 * `Slider` and for the dialog-label tint.
 */
type StatTone = "lime" | "blue" | "amber" | "violet";

const STAT_TONE_CSS: Record<StatTone, string> = {
  lime: "var(--color-pitch-lime)",
  blue: "rgb(120,170,255)",
  amber: "rgb(251,191,36)", // tailwind amber-400
  violet: "rgb(167,139,250)", // tailwind violet-400
};

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

/* ─────────────────────────────── page ─────────────────────────────── */

/**
 * Spielermanager — local-first roster manager. Cards show avatar, jersey,
 * position pill and the four stat bars. Click a card to open a detail/edit
 * dialog; "+" opens an empty form. Image is stored inline as a data URL in
 * `image_ref` so it round-trips through the sync engine with the rest of the
 * player payload (Phase 1, no separate blob channel).
 */
export function PlayersPage() {
  const { db, orgId, defaultTeamId, engine, status } = useSync();
  const qc = useQueryClient();

  const ready = Boolean(db && orgId && defaultTeamId);

  const players = useQuery<PlayerRow[]>({
    queryKey: ["players", orgId, defaultTeamId],
    enabled: ready,
    queryFn: () => listPlayers(db!, orgId!, defaultTeamId!),
    refetchInterval: 5000,
  });

  const [search, setSearch] = useState("");
  const [posFilter, setPosFilter] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<PlayerRow | null>(null);

  const create = useMutation({
    mutationFn: async (input: PlayerFormValue) => {
      if (!ready) throw new Error("Lokale Datenbank nicht bereit.");
      await createPlayer(db!, {
        orgId: orgId!,
        ownerKind: "team",
        ownerId: defaultTeamId!,
        name: input.name,
        position: input.position,
        foot: input.foot,
        shirtNumber: input.shirtNumber,
        email: input.email,
        notes: input.notes,
        imageRef: input.imageRef,
        stats: input.stats,
      });
      void engine?.syncNow();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["players"], exact: false });
      setCreating(false);
    },
  });

  const save = useMutation({
    mutationFn: async (args: { id: string; input: PlayerFormValue }) => {
      if (!ready) throw new Error("Lokale Datenbank nicht bereit.");
      await updatePlayer(db!, {
        id: args.id,
        orgId: orgId!,
        name: args.input.name,
        position: args.input.position,
        foot: args.input.foot,
        shirtNumber: args.input.shirtNumber,
        email: args.input.email,
        notes: args.input.notes,
        imageRef: args.input.imageRef,
        stats: args.input.stats,
      });
      void engine?.syncNow();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["players"], exact: false });
      setEditing(null);
    },
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      if (!ready) throw new Error("Lokale Datenbank nicht bereit.");
      await deletePlayer(db!, { id, orgId: orgId! });
      void engine?.syncNow();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["players"], exact: false });
      setEditing(null);
    },
  });

  const list = players.data ?? [];
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return list.filter((p) => {
      if (posFilter && p.position !== posFilter) return false;
      if (!q) return true;
      return (
        p.name.toLowerCase().includes(q) ||
        String(p.shirt_number ?? "").includes(q) ||
        (p.position?.toLowerCase() ?? "").includes(q)
      );
    });
  }, [list, search, posFilter]);

  const activePositions = useMemo(() => {
    const s = new Set<string>();
    for (const p of list) if (p.position) s.add(p.position);
    return POSITIONS.filter((pos) => s.has(pos));
  }, [list]);

  const { confirm, dialog: confirmDialog } = useConfirm();

  return (
    <div className="flex flex-col gap-5">
      {/* ── toolbar ────────────────────────────────────────────── */}
      <section className="glass-floating flex flex-wrap items-center gap-3 rounded-3xl px-6 py-5 sm:px-7">
        <div className="min-w-0 flex-1">
          <h1 className="home-display text-[20px] font-bold" style={{ color: "#f4f5f3" }}>
            Spielermanager
          </h1>
          <p className="text-[12px]" style={{ color: "var(--color-on-surface-variant)" }}>
            {ready
              ? `${list.length} Spieler im Kader${status.kind === "idle" ? "" : ` · ${status.kind}`}`
              : "Lade lokale Datenbank…"}
          </p>
        </div>

        <label className="relative flex h-10 min-w-50 flex-1 items-center sm:max-w-70">
          <Search
            className="pointer-events-none absolute left-3 size-4 text-muted-foreground"
            aria-hidden
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Suche Name, Nummer, Position…"
            className="h-full w-full rounded-full border border-white/15 bg-white/4 pl-9 pr-3 text-body-sm outline-none focus:border-pitch-lime/60 focus:shadow-glow"
          />
        </label>

        <button
          type="button"
          onClick={() => setCreating(true)}
          disabled={!ready}
          className="inline-flex h-10 items-center gap-2 rounded-full bg-pitch-lime px-4 text-body-sm font-semibold text-[#1d3700] transition-shadow hover:shadow-glow disabled:opacity-60"
        >
          <Plus className="size-4" />
          Neuer Spieler
        </button>
      </section>

      {/* ── filter chips ───────────────────────────────────────── */}
      {activePositions.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-label-caps uppercase tracking-widest text-muted-foreground">
            Filter
          </span>
          <FilterChip label="Alle" active={posFilter === null} onClick={() => setPosFilter(null)} />
          {activePositions.map((pos) => (
            <FilterChip
              key={pos}
              label={pos}
              tone={toneOf(pos)}
              active={posFilter === pos}
              onClick={() => setPosFilter(posFilter === pos ? null : pos)}
            />
          ))}
        </div>
      )}

      {/* ── grid ───────────────────────────────────────────────── */}
      {!ready ? null : list.length === 0 ? (
        <EmptyState onAdd={() => setCreating(true)} />
      ) : visible.length === 0 ? (
        <Glass padding="lg" className="text-body-md text-muted-foreground">
          Keine Spieler entsprechen deiner Suche.
        </Glass>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
          {visible.map((p) => (
            <PlayerCard key={p.id} player={p} onOpen={() => setEditing(p)} />
          ))}
        </div>
      )}

      {/* ── dialogs ────────────────────────────────────────────── */}
      {creating && (
        <PlayerDialog
          mode="create"
          initial={null}
          busy={create.isPending}
          onSubmit={(v) => create.mutate(v)}
          onClose={() => setCreating(false)}
        />
      )}
      {editing && (
        <PlayerDialog
          mode="edit"
          initial={editing}
          busy={save.isPending || remove.isPending}
          onSubmit={(v) => save.mutate({ id: editing.id, input: v })}
          onDelete={async () => {
            const ok = await confirm({
              title: "Spieler entfernen?",
              message: `„${editing.name}“ wird unwiderruflich gelöscht.`,
              confirmLabel: "Entfernen",
            });
            if (ok) remove.mutate(editing.id);
          }}
          onClose={() => setEditing(null)}
        />
      )}
      {confirmDialog}
    </div>
  );
}

/* ──────────────────────────── empty state ───────────────────────────── */

function EmptyState({ onAdd }: { onAdd: () => void }) {
  return (
    <Glass padding="lg" className="flex flex-col items-center gap-4 py-12 text-center">
      <div className="grid size-16 place-items-center rounded-full bg-white/4 ring-1 ring-white/10">
        <Users className="size-7 text-pitch-lime" aria-hidden />
      </div>
      <div className="flex flex-col gap-1">
        <h2 className="text-title-md font-semibold">Noch kein Kader</h2>
        <p className="max-w-md text-body-sm text-muted-foreground">
          Lege deinen ersten Spieler an. Alles wird lokal gespeichert und synchronisiert sich
          automatisch auf deine anderen Geräte, sobald sie online sind.
        </p>
      </div>
      <button
        type="button"
        onClick={onAdd}
        className="inline-flex h-10 items-center gap-2 rounded-full bg-pitch-lime px-4 text-body-sm font-semibold text-[#1d3700] hover:shadow-glow"
      >
        <Plus className="size-4" />
        Ersten Spieler anlegen
      </button>
    </Glass>
  );
}

/* ──────────────────────────── chip ─────────────────────────────────── */

function FilterChip({
  label,
  active,
  tone,
  onClick,
}: {
  label: string;
  active: boolean;
  tone?: { bg: string; fg: string; ring: string };
  onClick: () => void;
}) {
  const t = tone ?? NEUTRAL_TONE;
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex h-7 items-center rounded-full px-3 text-label-caps uppercase tracking-widest transition-all"
      style={{
        background: active ? t.bg : "rgba(255,255,255,0.04)",
        color: active ? t.fg : "var(--color-on-surface-variant)",
        boxShadow: active ? `inset 0 0 0 1px ${t.ring}` : "inset 0 0 0 1px rgba(255,255,255,0.10)",
      }}
    >
      {label}
    </button>
  );
}

/* ──────────────────────────── card ─────────────────────────────────── */

function PlayerCard({ player, onOpen }: { player: PlayerRow; onOpen: () => void }) {
  const stats = parseStats(player.stats_json);
  const tone = toneOf(player.position);
  const avg = avgStats(stats);

  // Vertical "portrait card" layout (v1-inspired): zentrierter Avatar mit
  // Trikot-Badge als Overlay, darunter Name + Position-Chips, dann die
  // overall rating und vier stacked stat bars. Optisch größer als der alte
  // horizontale Cardtyp — die Grid-Spaltenzahl bleibt gleich, die Karten
  // werden also natürlich breiter & höher.
  return (
    <button
      type="button"
      onClick={onOpen}
      className="group glass-floating relative flex flex-col items-center gap-4 rounded-3xl p-6 text-center transition-shadow hover:shadow-glow"
    >
      {/* Avatar mit Trikot-Badge als Overlay */}
      <div className="relative mt-1">
        <Avatar src={player.image_ref} name={player.name} ring={tone.ring} size={112} />
        {player.shirt_number != null && (
          <div
            className="absolute -bottom-1 -right-1 grid size-10 place-items-center rounded-full text-body-md font-bold tabular-nums"
            style={{
              // Solider dunkler Untergrund — sonst scheint das Avatar-Bild
              // durch die getintete Position-Farbe und die Nummer wird
              // unleserlich. Ring + Text behalten die Position-Tönung.
              background: "#1a1d20",
              color: tone.fg,
              boxShadow: `inset 0 0 0 1.5px ${tone.ring}, 0 2px 8px rgba(0,0,0,0.5)`,
            }}
          >
            {player.shirt_number}
          </div>
        )}
      </div>

      {/* Name + Position/Foot Chips */}
      <div className="flex flex-col items-center gap-2">
        <span className="text-title-lg font-semibold leading-tight">{player.name}</span>
        <div className="flex flex-wrap items-center justify-center gap-1.5">
          {player.position && (
            <span
              className="inline-flex h-5 items-center rounded-full px-2 text-label-caps uppercase tracking-widest"
              style={{ background: tone.bg, color: tone.fg }}
            >
              {player.position}
            </span>
          )}
          {player.foot && (
            <span className="inline-flex h-5 items-center rounded-full bg-white/6 px-2 text-label-caps uppercase tracking-widest text-muted-foreground">
              {player.foot}
            </span>
          )}
        </div>
      </div>

      {/* Overall rating — big number, position-tinted */}
      {avg != null && (
        <div className="flex flex-col items-center leading-none">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
            Gesamt
          </span>
          <span className="mt-1 text-3xl font-bold tabular-nums" style={{ color: tone.fg }}>
            {avg}
          </span>
        </div>
      )}

      {/* Vier gestackte Stat-Bars (volle Kartenbreite). Jede Eigenschaft
          bekommt eine eigene Farbe — dieselbe Zuordnung wird auch im
          Edit-Dialog verwendet, damit Card und Slider visuell konsistent
          sind. */}
      <div className="flex w-full flex-col gap-2.5">
        <FullStat label="Ausdauer" value={stats.stamina} tone="lime" />
        <FullStat label="Spielintelligenz" value={stats.gameIntelligence} tone="blue" />
        <FullStat label="Teamgeist" value={stats.teamSpirit} tone="amber" />
        <FullStat label="Technik" value={stats.technique} tone="violet" />
      </div>

      {/* Hover-Footer */}
      <div className="flex w-full items-center justify-center border-t border-white/5 pt-3 text-label-caps uppercase tracking-widest text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100">
        Bearbeiten →
      </div>
    </button>
  );
}

function FullStat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number | null | undefined;
  tone: StatTone;
}) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between text-[11px]">
        <span className="uppercase tracking-widest text-muted-foreground">{label}</span>
        <span className="font-semibold tabular-nums text-white/80">{value ?? 0}</span>
      </div>
      <StatBar value={value ?? 0} tone={tone} showValue={false} label="" />
    </div>
  );
}

function avgStats(s: PlayerStats): number | null {
  const xs = [s.stamina, s.technique, s.gameIntelligence, s.teamSpirit].filter(
    (v): v is number => typeof v === "number",
  );
  if (xs.length === 0) return null;
  return Math.round(xs.reduce((a, b) => a + b, 0) / xs.length);
}

/* ──────────────────────────── avatar ───────────────────────────────── */

function Avatar({
  src,
  name,
  ring,
  size,
}: {
  src: string | null;
  name: string;
  ring: string;
  size: number;
}) {
  return (
    <div
      className="relative grid shrink-0 place-items-center overflow-hidden rounded-full bg-white/4 font-semibold"
      style={{
        width: size,
        height: size,
        boxShadow: `inset 0 0 0 2px ${ring}`,
        fontSize: size * 0.35,
      }}
    >
      {src ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img src={src} alt={name} className="size-full object-cover" draggable={false} />
      ) : (
        <span style={{ color: "var(--color-on-surface-variant)" }}>{initials(name)}</span>
      )}
    </div>
  );
}

/* ──────────────────────────── dialog ───────────────────────────────── */

interface PlayerFormValue {
  name: string;
  position: string | null;
  foot: string | null;
  shirtNumber: number | null;
  email: string | null;
  notes: string | null;
  imageRef: string | null;
  stats: PlayerStats;
}

function rowToFormValue(row: PlayerRow | null): PlayerFormValue {
  const stats = row ? parseStats(row.stats_json) : {};
  return {
    name: row?.name ?? "",
    position: row?.position ?? null,
    foot: row?.foot ?? null,
    shirtNumber: row?.shirt_number ?? null,
    email: row?.email ?? null,
    notes: row?.notes ?? null,
    imageRef: row?.image_ref ?? null,
    stats: {
      stamina: stats.stamina ?? 70,
      technique: stats.technique ?? 60,
      gameIntelligence: stats.gameIntelligence ?? 60,
      teamSpirit: stats.teamSpirit ?? 70,
    },
  };
}

function PlayerDialog({
  mode,
  initial,
  busy,
  onSubmit,
  onClose,
  onDelete,
}: {
  mode: "create" | "edit";
  initial: PlayerRow | null;
  busy: boolean;
  onSubmit: (v: PlayerFormValue) => void;
  onClose: () => void;
  onDelete?: () => void;
}) {
  const [form, setForm] = useState<PlayerFormValue>(() => rowToFormValue(initial));
  const [imageError, setImageError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  function patch<K extends keyof PlayerFormValue>(key: K, value: PlayerFormValue[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }
  function patchStat<K extends keyof PlayerStats>(key: K, value: PlayerStats[K]) {
    setForm((f) => ({ ...f, stats: { ...f.stats, [key]: value } }));
  }

  async function onPickImage(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageError(null);
    if (!/^image\/(png|jpe?g|webp|avif)$/i.test(file.type)) {
      setImageError("Bitte PNG, JPG, WebP oder AVIF wählen.");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setImageError("Maximal 2 MB pro Bild.");
      return;
    }
    const dataUrl = await readAsDataUrl(file);
    patch("imageRef", dataUrl);
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!form.name.trim()) return;
    onSubmit({
      ...form,
      name: form.name.trim(),
      email: form.email?.trim() ? form.email.trim() : null,
      notes: form.notes?.trim() ? form.notes.trim() : null,
      position: form.position ?? null,
      foot: form.foot ?? null,
    });
  }

  const tone = toneOf(form.position);

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur-sm p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <Glass
        level="floating"
        padding="lg"
        className="relative flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden"
      >
        {/* header */}
        <header className="flex items-start justify-between gap-3 pb-4">
          <div className="flex flex-col gap-1">
            <span className="text-label-caps uppercase tracking-widest text-pitch-lime">
              {mode === "create" ? "Neuer Spieler" : "Spieler bearbeiten"}
            </span>
            <h2 className="text-display-sm font-semibold tracking-tight">
              {form.name || "Unbenannt"}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Schließen"
            className="grid size-9 place-items-center rounded-full border border-white/10 text-muted-foreground hover:border-white/25 hover:text-foreground"
          >
            <X className="size-4" />
          </button>
        </header>

        <form onSubmit={submit} className="flex min-h-0 flex-col gap-5 overflow-y-auto pr-1">
          {/* identity row */}
          <div className="grid gap-5 sm:grid-cols-[180px_1fr]">
            <div className="flex flex-col items-center gap-3">
              <div className="relative">
                <Avatar src={form.imageRef} name={form.name || "?"} ring={tone.ring} size={140} />
                <button
                  type="button"
                  onClick={() => fileInput.current?.click()}
                  aria-label="Bild hochladen"
                  className="absolute -bottom-1 -right-1 grid size-9 place-items-center rounded-full bg-pitch-lime text-[#1d3700] shadow-lg hover:shadow-glow"
                >
                  <Camera className="size-4" />
                </button>
              </div>
              <input
                ref={fileInput}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/avif"
                hidden
                onChange={onPickImage}
              />
              {form.imageRef && (
                <button
                  type="button"
                  onClick={() => patch("imageRef", null)}
                  className="text-label-caps uppercase tracking-widest text-muted-foreground hover:text-tactical-red"
                >
                  Bild entfernen
                </button>
              )}
              {imageError && <p className="text-body-sm text-tactical-red">{imageError}</p>}
            </div>

            <div className="flex flex-col gap-4">
              <Field label="Name" required>
                <input
                  value={form.name}
                  onChange={(e) => patch("name", e.target.value)}
                  required
                  autoFocus
                  placeholder="Vor- und Nachname"
                  className="h-11 w-full rounded-md border border-white/15 bg-white/4 px-3 text-body-md outline-none focus:border-pitch-lime/60 focus:shadow-glow"
                />
              </Field>

              <div className="grid gap-4 sm:grid-cols-[120px_1fr]">
                <Field label="Nummer">
                  <input
                    type="number"
                    min={1}
                    max={99}
                    value={form.shirtNumber ?? ""}
                    onChange={(e) =>
                      patch(
                        "shirtNumber",
                        e.target.value
                          ? Math.max(1, Math.min(99, Number.parseInt(e.target.value, 10) || 0))
                          : null,
                      )
                    }
                    placeholder="–"
                    className="h-11 w-full rounded-md border border-white/15 bg-white/4 px-3 text-body-md tabular-nums outline-none focus:border-pitch-lime/60 focus:shadow-glow"
                  />
                </Field>
                <Field label="Fuß">
                  <PillGroup
                    options={FEET as readonly string[]}
                    value={form.foot}
                    onChange={(v) => patch("foot", v)}
                  />
                </Field>
              </div>

              <Field label="Position">
                <PillGroup
                  options={POSITIONS as readonly string[]}
                  value={form.position}
                  onChange={(v) => patch("position", v)}
                  toneOf={toneOf}
                />
              </Field>
            </div>
          </div>

          {/* stats */}
          <section className="flex flex-col gap-3">
            <h3 className="flex items-center gap-2 text-label-caps uppercase tracking-widest text-muted-foreground">
              <Shield className="size-3.5" />
              Attribute
            </h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <Slider
                label="Ausdauer"
                value={form.stats.stamina ?? 0}
                onChange={(v) => patchStat("stamina", v)}
                tone="lime"
              />
              <Slider
                label="Spielintelligenz"
                value={form.stats.gameIntelligence ?? 0}
                onChange={(v) => patchStat("gameIntelligence", v)}
                tone="blue"
              />
              <Slider
                label="Teamgeist"
                value={form.stats.teamSpirit ?? 0}
                onChange={(v) => patchStat("teamSpirit", v)}
                tone="amber"
              />
              <Slider
                label="Technik"
                value={form.stats.technique ?? 0}
                onChange={(v) => patchStat("technique", v)}
                tone="violet"
              />
            </div>
          </section>

          {/* contact + notes */}
          <section className="flex flex-col gap-4">
            <Field label="E-Mail">
              <div className="relative">
                <Mail
                  className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                  aria-hidden
                />
                <input
                  type="email"
                  value={form.email ?? ""}
                  onChange={(e) => patch("email", e.target.value)}
                  placeholder="spieler@beispiel.de"
                  className="h-11 w-full rounded-md border border-white/15 bg-white/4 pl-10 pr-3 text-body-md outline-none focus:border-pitch-lime/60 focus:shadow-glow"
                />
              </div>
            </Field>
            <Field label="Notizen">
              <textarea
                value={form.notes ?? ""}
                onChange={(e) => patch("notes", e.target.value)}
                rows={4}
                placeholder="Hinweise zu Training, Verfügbarkeit, Verletzungen…"
                className="w-full resize-y rounded-md border border-white/15 bg-white/4 px-3 py-2.5 text-body-md outline-none focus:border-pitch-lime/60 focus:shadow-glow"
              />
            </Field>
          </section>
        </form>

        {/* footer */}
        <footer className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-white/10 pt-4">
          {mode === "edit" && onDelete ? (
            <button
              type="button"
              onClick={onDelete}
              disabled={busy}
              className="inline-flex h-10 items-center gap-2 rounded-full border border-tactical-red/40 px-4 text-body-sm text-tactical-red hover:bg-tactical-red/10 disabled:opacity-60"
            >
              <Trash2 className="size-4" />
              Entfernen
            </button>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="inline-flex h-10 items-center rounded-full border border-white/15 px-4 text-body-sm text-muted-foreground hover:border-white/30 hover:text-foreground disabled:opacity-60"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              onClick={submit}
              disabled={busy || !form.name.trim()}
              className="inline-flex h-10 items-center gap-2 rounded-full bg-pitch-lime px-5 text-body-sm font-semibold text-[#1d3700] transition-shadow hover:shadow-glow disabled:opacity-60"
            >
              {busy ? (
                "Speichere…"
              ) : (
                <>
                  <Pencil className="size-4" />
                  {mode === "create" ? "Spieler anlegen" : "Änderungen speichern"}
                </>
              )}
            </button>
          </div>
        </footer>
      </Glass>
    </div>
  );
}

/* ──────────────────────────── form atoms ───────────────────────────── */

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-label-caps uppercase tracking-widest text-muted-foreground">
        {label}
        {required && <span className="text-pitch-lime"> *</span>}
      </span>
      {children}
    </label>
  );
}

function PillGroup({
  options,
  value,
  onChange,
  toneOf: toneFn,
}: {
  options: readonly string[];
  value: string | null;
  onChange: (v: string | null) => void;
  toneOf?: (v: string) => { bg: string; fg: string; ring: string };
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((opt) => {
        const active = value === opt;
        const t = toneFn ? toneFn(opt) : NEUTRAL_TONE;
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(active ? null : opt)}
            className="inline-flex h-8 items-center rounded-full px-3 text-label-caps uppercase tracking-widest transition-all"
            style={{
              background: active ? t.bg : "rgba(255,255,255,0.04)",
              color: active ? t.fg : "var(--color-on-surface-variant)",
              boxShadow: active
                ? `inset 0 0 0 1px ${t.ring}`
                : "inset 0 0 0 1px rgba(255,255,255,0.10)",
            }}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

function Slider({
  label,
  value,
  onChange,
  tone,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  tone: StatTone;
}) {
  const fill = STAT_TONE_CSS[tone];
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between">
        <span className="text-label-caps uppercase tracking-widest" style={{ color: fill }}>
          {label}
        </span>
        <span className="text-body-md font-semibold tabular-nums">{value}</span>
      </div>
      <input
        type="range"
        min={0}
        max={100}
        value={value}
        onChange={(e) => onChange(Number.parseInt(e.target.value, 10))}
        className="h-2 w-full cursor-pointer appearance-none rounded-full bg-[#2D2D2D]"
        style={{
          background: `linear-gradient(to right, ${fill} 0%, ${fill} ${value}%, #2D2D2D ${value}%, #2D2D2D 100%)`,
        }}
      />
    </div>
  );
}

/* ──────────────────────────── helpers ──────────────────────────────── */

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}
